/**
 * Pure helpers to turn live bookings into `ScheduleEvent`s for the schedule
 * page and the Home schedule snapshot. Most booking milestones (ETD / cutoff /
 * stuffing) are date-only, so those events are stacked within their weekday
 * column. When a source value carries a real clock time it is used for ordering
 * and shown in the event label. Kept free of React/date-library deps so it is
 * unit-testable.
 */
import { resolvePartyNameDisplay } from "@/lib/bookingPartyDisplay";
import {
  bookingCutoff,
  bookingEtd,
  bookingEta,
  bookingStuffing,
} from "@/lib/bookingKeyDates";
import { toLocalIsoDate } from "@/lib/dateHorizon";
import { formatPortLabel, formatRouteLabel } from "@/lib/formatPort";
import type { MockBooking } from "@/types/booking";
import type {
  EventKind,
  ScheduleEvent,
  ScheduleEventBookingRef,
  ScheduleEventOriginGroup,
  ScheduleMilestone,
  WeekDay,
} from "@/types/schedule";

export type { ScheduleMilestone } from "@/types/schedule";

const MILESTONE_KIND: Record<ScheduleMilestone, EventKind> = {
  departure: "departure",
  arrival: "arrival",
  cutoff: "cutoff",
  stuffing: "stuffing",
};

/** Word appended to the event title, e.g. "MSC Shanghai ETD". */
const MILESTONE_TITLE_WORD: Record<ScheduleMilestone, string> = {
  departure: "ETD",
  arrival: "ETA",
  cutoff: "cutoff",
  stuffing: "stuffing",
};

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * Parse a `YYYY-MM-DD` or ISO datetime string into a local `Date` at midnight,
 * avoiding the UTC shift that `new Date("YYYY-MM-DD")` introduces. Returns
 * `null` for empty/invalid input.
 */
export function parseLocalDate(value?: string | null): Date | null {
  if (!value) return null;
  const dateOnly = value.length >= 10 ? value.slice(0, 10) : value;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateOnly);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

/**
 * Minutes-since-midnight for an ISO datetime string, or `null` when the value
 * is date-only or has no meaningful time-of-day. A bare midnight (`00:00`) is
 * treated as date-only, since backends persist date-only milestones that way.
 */
export function parseTimeOfDay(value?: string | null): number | null {
  if (!value) return null;
  const match = /T(\d{2}):(\d{2})/.exec(value);
  if (!match) return null;
  const minutes = Number(match[1]) * 60 + Number(match[2]);
  return minutes === 0 ? null : minutes;
}

/** The source milestone string on a booking (operational values preferred). */
function milestoneSource(
  booking: MockBooking,
  milestone: ScheduleMilestone,
): string | undefined {
  switch (milestone) {
    case "departure":
      return bookingEtd(booking);
    case "arrival":
      return bookingEta(booking);
    case "cutoff":
      return bookingCutoff(booking);
    case "stuffing":
      return bookingStuffing(booking);
    default:
      return undefined;
  }
}

/** Zero-based day offset (0 = Monday) of `date` within the week starting `monday`. */
function dayOffset(monday: Date, date: Date): number {
  return Math.round((date.getTime() - monday.getTime()) / 86_400_000);
}

function formatMonthDay(date: Date): string {
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatTime(minutes: number): string {
  const h = String(Math.floor(minutes / 60)).padStart(2, "0");
  const m = String(minutes % 60).padStart(2, "0");
  return `${h}:${m}`;
}

/** Build the Mon–Sun `WeekDay[]` header for the week starting at `monday`. */
export function buildWeekDays(monday: Date): WeekDay[] {
  return WEEKDAY_LABELS.map((label, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return { label, date: String(d.getDate()) };
  });
}

/** Human label for the week span, e.g. "Aug 24 – 30, 2026". */
export function weekRangeLabel(monday: Date): string {
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const left = monday.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
  const right = sunday.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return `${left} – ${right}`;
}

interface Candidate {
  booking: MockBooking;
  milestone: ScheduleMilestone;
  date: Date;
  minutes: number | null;
}

/** Flatten bookings × milestones into dated candidates (no range filtering). */
function collectCandidates(
  bookings: MockBooking[],
  milestones: ScheduleMilestone[],
): Candidate[] {
  const out: Candidate[] = [];
  for (const booking of bookings) {
    for (const milestone of milestones) {
      const source = milestoneSource(booking, milestone);
      const date = parseLocalDate(source);
      if (!date) continue;
      out.push({ booking, milestone, date, minutes: parseTimeOfDay(source) });
    }
  }
  return out;
}

/** The port a milestone is anchored to (arrivals: destination; else origin). */
function anchorPortOf(c: Candidate) {
  return c.milestone === "arrival"
    ? c.booking.destination_port
    : c.booking.origin_port;
}

function candidateVessel(c: Candidate): string | undefined {
  return c.booking.vessel ?? c.booking.operational?.vessel ?? undefined;
}

/**
 * Identity of an event as seen by operations. ETD / ETA / cutoff collapse by
 * carrier + anchor port + date + time (e.g. "MSC Haifa ETA · Aug 23").
 * Stuffing collapses per carrier per calendar day so the grid stays readable
 * when many origins stuff on the same sailing day.
 */
function candidateKey(c: Candidate): string {
  const carrier = (c.booking.carrier ?? "").toUpperCase();
  if (c.milestone === "stuffing") {
    return [c.milestone, carrier, toLocalIsoDate(c.date)].join("|");
  }
  return [
    c.milestone,
    carrier,
    anchorPortOf(c).locode,
    toLocalIsoDate(c.date),
    c.minutes ?? "d",
  ].join("|");
}

/**
 * Group candidates by {@link candidateKey}, preserving first-seen order. Each
 * group becomes one aggregated event card.
 */
function groupCandidates(candidates: Candidate[]): Candidate[][] {
  const groups = new Map<string, Candidate[]>();
  for (const c of candidates) {
    const key = candidateKey(c);
    const bucket = groups.get(key);
    if (bucket) bucket.push(c);
    else groups.set(key, [c]);
  }
  return [...groups.values()];
}

const UNKNOWN_SHIPPER = "Unknown shipper";

function shipperLabel(booking: MockBooking): string {
  return (
    resolvePartyNameDisplay(booking.shipper_name, booking.shipper) ??
    UNKNOWN_SHIPPER
  );
}

function compareLabel(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: "base" });
}

function laneLabel(group: Candidate[], showLocodes: boolean): string {
  const lanes = new Set(
    group.map((c) =>
      formatRouteLabel(
        c.booking.origin_port,
        c.booking.destination_port,
        showLocodes,
      ),
    ),
  );
  if (lanes.size === 1) return [...lanes][0];
  if (group[0].milestone === "stuffing") {
    const origins = new Set(
      group.map((c) => formatPortLabel(c.booking.origin_port, showLocodes)),
    );
    if (origins.size > 1) return `${origins.size} origins`;
  }
  return `${lanes.size} lanes`;
}

/** Nest stuffing bookings as origin port → shipper → booking. */
function buildStuffingGroups(
  group: Candidate[],
  showLocodes: boolean,
): ScheduleEventOriginGroup[] {
  const byOrigin = new Map<string, Candidate[]>();
  for (const c of group) {
    const key = c.booking.origin_port.locode || c.booking.origin_port.name;
    const bucket = byOrigin.get(key);
    if (bucket) bucket.push(c);
    else byOrigin.set(key, [c]);
  }

  return [...byOrigin.values()]
    .map((originCandidates) => {
      const port = originCandidates[0].booking.origin_port;
      const byShipper = new Map<string, Candidate[]>();
      for (const c of originCandidates) {
        const shipper = shipperLabel(c.booking);
        const bucket = byShipper.get(shipper);
        if (bucket) bucket.push(c);
        else byShipper.set(shipper, [c]);
      }

      const shippers = [...byShipper.entries()]
        .sort(([a], [b]) => {
          if (a === UNKNOWN_SHIPPER) return 1;
          if (b === UNKNOWN_SHIPPER) return -1;
          return compareLabel(a, b);
        })
        .map(([shipper, candidates]) => {
          const seen = new Set<string>();
          const bookings: ScheduleEventBookingRef[] = [];
          for (const c of candidates) {
            const id = c.booking.source_id;
            if (seen.has(id)) continue;
            seen.add(id);
            bookings.push({
              id,
              destination: formatPortLabel(
                c.booking.destination_port,
                showLocodes,
              ),
            });
          }
          bookings.sort((a, b) => compareLabel(a.id, b.id));
          return { shipper, bookings };
        });

      return {
        origin: formatPortLabel(port, showLocodes),
        locode: port.locode,
        shippers,
        bookingCount: shippers.reduce((n, s) => n + s.bookings.length, 0),
      };
    })
    .sort((a, b) => compareLabel(a.origin, b.origin));
}

/** Build a (possibly aggregated) `ScheduleEvent` from a group of candidates. */
function toGroupedEvent(
  group: Candidate[],
  day: number,
  startSlot: number,
  showLocodes: boolean,
): ScheduleEvent {
  const rep = group[0];
  const carrier = rep.booking.carrier ?? "";
  const anchorName = anchorPortOf(rep).name || anchorPortOf(rep).locode;
  const hasTime = rep.minutes != null;
  const timeLabel = hasTime ? formatTime(rep.minutes!) : formatMonthDay(rep.date);

  const bookingIds = [...new Set(group.map((c) => c.booking.source_id))];
  const lane = laneLabel(group, showLocodes);

  const vessels = new Set(
    group.map(candidateVessel).filter((v): v is string => Boolean(v)),
  );
  const agents = new Set(
    group.map((c) => c.booking.agent).filter((v): v is string => Boolean(v)),
  );

  const title =
    rep.milestone === "stuffing"
      ? `${carrier} ${MILESTONE_TITLE_WORD.stuffing}`.trim()
      : `${carrier} ${anchorName} ${MILESTONE_TITLE_WORD[rep.milestone]}`.trim();

  return {
    id: candidateKey(rep),
    title,
    time: timeLabel,
    day,
    startSlot,
    span: 1,
    kind: MILESTONE_KIND[rep.milestone],
    carrier,
    lane,
    bookingId: bookingIds.length === 1 ? bookingIds[0] : undefined,
    bookingIds,
    count: bookingIds.length,
    milestone: rep.milestone,
    dateIso: toLocalIsoDate(rep.date),
    vessel: vessels.size === 1 ? [...vessels][0] : undefined,
    agent: agents.size === 1 ? [...agents][0] : undefined,
    hasTime,
    groups:
      rep.milestone === "stuffing"
        ? buildStuffingGroups(group, showLocodes)
        : undefined,
  };
}

/**
 * Within a single day: date-only (null minutes → -1) before timed, then by
 * clock time. Both candidates are assumed to share a calendar day.
 */
function compareWithinDay(a: Candidate, b: Candidate): number {
  return (a.minutes ?? -1) - (b.minutes ?? -1);
}

/** Chronological across days: by calendar day, then by within-day order. */
function compareByDateTime(a: Candidate, b: Candidate): number {
  return a.date.getTime() - b.date.getTime() || compareWithinDay(a, b);
}

interface MapOptions {
  monday: Date;
  milestones: ScheduleMilestone[];
  /**
   * Maximum events rendered per weekday column. Extra events are dropped
   * (used by the compact Home snapshot). Omit for the full page.
   */
  maxEventsPerDay?: number;
  /** When true, lanes use UN/LOCODEs; otherwise port names. Default true. */
  showLocodes?: boolean;
}

/**
 * Map bookings into stacked schedule events for the week starting at `monday`.
 * Within each weekday, date-only events sort to the top and timed events follow
 * in chronological order; events are then stacked into sequential rows.
 */
export function bookingsToScheduleEvents(
  bookings: MockBooking[],
  { monday, milestones, maxEventsPerDay, showLocodes = true }: MapOptions,
): ScheduleEvent[] {
  const inWeek = collectCandidates(bookings, milestones).filter((c) => {
    const day = dayOffset(monday, c.date);
    return day >= 0 && day <= 6;
  });

  const groups = groupCandidates(inWeek)
    .map((group) => ({ group, day: dayOffset(monday, group[0].date) }))
    .sort((a, b) => a.day - b.day || compareWithinDay(a.group[0], b.group[0]));

  const perDayCount = new Array(7).fill(0);
  const events: ScheduleEvent[] = [];
  for (const { group, day } of groups) {
    const slot = perDayCount[day];
    if (maxEventsPerDay != null && slot >= maxEventsPerDay) continue;
    perDayCount[day] += 1;
    events.push(toGroupedEvent(group, day, slot, showLocodes));
  }
  return events;
}

/**
 * Flat, chronologically-sorted schedule events within `[from, to]` (inclusive,
 * local `YYYY-MM-DD`). Used by the day and month views, which lay events out by
 * `dateIso` rather than by a single week's day columns.
 */
export function bookingsToScheduleItems(
  bookings: MockBooking[],
  milestones: ScheduleMilestone[],
  range: { from: string; to: string },
  showLocodes = true,
): ScheduleEvent[] {
  const fromT = parseLocalDate(range.from)?.getTime() ?? -Infinity;
  const toT = parseLocalDate(range.to)?.getTime() ?? Infinity;
  const inRange = collectCandidates(bookings, milestones).filter((c) => {
    const t = c.date.getTime();
    return t >= fromT && t <= toT;
  });

  return groupCandidates(inRange)
    .sort((a, b) => compareByDateTime(a[0], b[0]))
    .map((group) => toGroupedEvent(group, 0, 0, showLocodes));
}

/** Tallest per-day stack across the given events (drives grid row count). */
export function maxStackDepth(events: ScheduleEvent[]): number {
  const perDay = new Array(7).fill(0);
  for (const ev of events) perDay[ev.day] += 1;
  return perDay.reduce((max, n) => Math.max(max, n), 0);
}
